<script lang="ts">
  import "@lottiefiles/lottie-player";
  import type { AnimationsConfig } from "../declarations/config";
  import { LottiePlayer, PlayerState } from "@lottiefiles/lottie-player";
  import {
    BackgroundDimensions,
    isScrolling,
    isDragging,
    selectedActivityIndex,
    isOtherActivitiesWindowOpen,
    assetsLoaded,
  } from "../store/AppState";
  import { backgroundDimensions } from "../config/dimensions";
  import { afterUpdate } from "svelte";
  import { soundsLoaded, playSound, playDialogue } from "../utils/AudioManager";
  import {
    sendInteractionEvent,
    INTERACTION_TYPE_ANIMATION,
  } from "../utils/analytics";

  let player: LottiePlayer;
  let idlePlayer: LottiePlayer;
  let hitbox: HTMLElement;

  export let config: AnimationsConfig;
  export let index: number;
  export let onLoad: () => void;

  const xRatio = Number(config.coordinates.x) / backgroundDimensions.width;
  const yRatio = Number(config.coordinates.y) / backgroundDimensions.height;
  const widthRatio = Number(config.width) / backgroundDimensions.width;
  const hitboxXRatio =
    Number(config.hitbox?.coordinates.x) / backgroundDimensions.width;
  const hitboxYRatio =
    Number(config.hitbox?.coordinates.y) / backgroundDimensions.height;
  const hitboxWidthRatio =
    Number(config.hitbox?.width) / backgroundDimensions.width;
  const hitboxHeightRatio =
    Number(config.hitbox?.height) / backgroundDimensions.height;

  let x = 0;
  let y = 0;
  let width = 0;

  let hitboxX = 0;
  let hitboxY = 0;
  let hitboxWidth = 0;
  let hitboxHeight = 0;

  const calculateDimensions = () => {
    x = xRatio * $BackgroundDimensions.width;
    y = yRatio * $BackgroundDimensions.height;
    width = widthRatio * $BackgroundDimensions.width;

    hitboxX = hitboxXRatio * $BackgroundDimensions.width;
    hitboxY = hitboxYRatio * $BackgroundDimensions.height;
    hitboxWidth = hitboxWidthRatio * $BackgroundDimensions.width;
    hitboxHeight = hitboxHeightRatio * $BackgroundDimensions.height;
  };

  window.addEventListener("resize", () => {
    calculateDimensions();
  });

  const handleKeydown = () => {};

  let showIdle = true;

  let firefoxPlayedToggle = false;
  let isFirefox = navigator.userAgent.includes("Firefox");
  const isPng = ["zajic", "pirat", "pleteni", "ententyk"].includes(config.name);

  let dialogueCounter = 0;

  const handlePlay = () => {
    if ($isDragging || !activeLoaded || !$soundsLoaded) {
      return;
    }
    if (!firefoxPlayedToggle && isFirefox && isPng) {
      return;
    }
    if (player.currentState === PlayerState.Playing) {
      return;
    }
    idlePlayer.stop();
    idlePlayer.seek(0);
    showIdle = false;
    player.seek(0);
    player.play();

    playSound(config.order - 1);
    const animationsWithDialogue = [3, 4, 6, 12, 13, 14, 16, 22];
    if (animationsWithDialogue.includes(config.order)) {
      const position = animationsWithDialogue.indexOf(config.order);
      playDialogue(position * 3, dialogueCounter);
      dialogueCounter++;
      if (dialogueCounter > 2) {
        dialogueCounter = 0;
      }
    }
    sendInteractionEvent(INTERACTION_TYPE_ANIMATION, config.name);
  };

  const handleLoad = (lottiePlayer: LottiePlayer, idle: boolean) => {
    lottiePlayer.getLottie().setSubframe(false);
    onLoad();

    if (idle) {
      idleLoaded = true;
    }
    if (!idle) {
      activeLoaded = true;
      hitbox.classList.remove("hitbox-loading");
    }

    if (isFirefox && isPng && !idle) {
      hitbox.classList.add("hitbox-loading");
      setTimeout(() => {
        player.classList.add("firefox-hack-player");
        player.play();
      }, 1000);
    }
  };

  const handleComplete = () => {
    player.stop();
    player.seek(0);
    showIdle = true;

    idlePlayer.play();
    if (hitbox.classList.contains("hitbox-loading")) {
      hitbox.classList.remove("hitbox-loading");
      player.classList.remove("firefox-hack-player");
      firefoxPlayedToggle = true;
    }

    if (config.name === "princeznicka") {
      lightsOut = true;
      setTimeout(() => {
        lightsOut = false;
      }, 1500);
    }
  };

  const handleEnterFrame = () => {
    const currentFrame = player.getLottie().currentFrame;
    if (config.name === "princeznicka") {
      if (
        [83, 86, 87, 88, 92, 93, 95].includes(currentFrame) ||
        currentFrame > 95
      ) {
        lightsOut = true;
      } else {
        lightsOut = false;
      }
    }
  };

  afterUpdate(() => {
    if (
      $isScrolling ||
      $selectedActivityIndex !== null ||
      $isOtherActivitiesWindowOpen
    ) {
      idlePlayer.pause();
    } else {
      idlePlayer.play();
    }
  });

  $: lightsOut = false;

  let idleLoaded = false;
  let activeLoaded = false;
</script>

<div class="animation-container" style={`--top: ${y}px; --left: ${x}px;`}>
  <div
    bind:this={hitbox}
    class="hitbox hitbox-loading"
    on:pointerup={() => handlePlay()}
    on:keydown={handleKeydown}
    tabindex={index}
    role="button"
    style={`width: ${hitboxWidth}px; height: ${hitboxHeight}px; top: ${hitboxY}px; left: ${hitboxX}px; z-index: ${
      config.layer ? config.layer + 20 : 0
    }; cursor: ${
      navigator.maxTouchPoints && navigator.maxTouchPoints > 2
        ? "default"
        : "pointer"
    }`}
  ></div>
  <div class="lottie-container" style={`--zIndex: ${config.layer};`}>
    <lottie-player
      bind:this={idlePlayer}
      src={`${__PUBLIC_PATH__}bodymovin/${config.name}/idle/${config.name}.json`}
      mode="normal"
      speed="1"
      on:load={() => handleLoad(idlePlayer, true)}
      autoplay={true}
      loop
      style={`width: ${width}px; display: ${
        showIdle === false ? "none" : "block"
      }`}
    />
    {#if idleLoaded && $assetsLoaded && $soundsLoaded}
      <lottie-player
        bind:this={player}
        on:load={() => handleLoad(player, false)}
        on:complete={() => handleComplete()}
        on:frame={() => handleEnterFrame()}
        src={`${__PUBLIC_PATH__}bodymovin/${config.name}/${config.name}.json`}
        mode="normal"
        speed="1"
        style={`width: ${width}px; display: ${
          showIdle === false ? "block" : "none"
        }`}
      />
    {/if}
  </div>
</div>
{#if lightsOut}
  <div class="lights-out" />
{/if}

<style>
  .animation-container {
    position: absolute;
    top: var(--top);
    left: var(--left);
  }

  .lottie-container {
    z-index: var(--zIndex);
    position: relative;
  }

  .hitbox {
    display: block;
    position: absolute;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  :global(.firefox-hack-player) {
    opacity: 0.000001%;
    position: fixed;
    left: 50%;
    top: 0;
    width: 1000px !important;
    height: 1000px !important;
    z-index: 100000 !important;
    display: block !important;
  }

  :global(.hitbox-loading) {
    cursor: wait !important;
  }

  .lights-out {
    width: 100%;
    height: 100%;
    z-index: 1000000;
    background-color: black;
    position: absolute;
    top: 0;
    left: 0;
  }
</style>
